<template>
  <div class="flex flex-col w-full overflow-y-scroll h-full">
    <div
      v-if="loading"
      class="flex flex-col w-full justify-center items-center h-[90vh]">
      <loader />
    </div>
    <div v-else-if="reviews.length && !loading" class="flex flex-col gap-4 w-full h-full">
      <t-card class="flex flex-col w-full">
        <section class="text-3xl font-medium text-gray-600 flex gap-2 justify-center items-center">
          <i class="pi pi-star-fill w-4 !text-xl" />
          <span>{{ reviewsSummary.averageRating }}</span>
        </section>
        <span class="text-gray-600 text-center my-1 text-sm">סה״כ {{ reviewsSummary.totalReviews }} דירוגים</span>
        <div v-for="reaction of <TReactions[]>['good','ok', 'not-good']" :key="reaction"
             class="flex justify-center items-center w-full gap-2 text-xs text-gray-600 mb-2">
          <span class="border border-gray-200 rounded py-1 px-1.5 flex-none w-16 text-center">{{
              rankMapping[reaction]
            }}</span>
          <div class="w-full">
            <ProgressBar :value="reviewsSummary.reactionsBreakdown[reaction].percentage"
                         :class="['!h-3 rounded-full', reaction]"
                         :show-value="false" />
          </div>
          <span class="flex-grow">({{ reviewsSummary.reactionsBreakdown[reaction].count }})</span>
        </div>
      </t-card>
      <t-card class="flex flex-col w-full">
        <transition-group
          mode="out-in"
          name="list"
          class="w-full h-full flex flex-col flex-grow gap-4"
          tag="div"
        >
          <div v-for="(review, index) of reviews"
               class="flex w-full"
               :class="{
                  'border-b pb-4': index !== reviews.length - 1
               }"
               :key="review.appointmentId">
            <div class="flex flex-col w-full">
              <div class="flex justify-between items-center pe-3">
                <span class="text-sm font-medium">{{ DateTime.fromISO(review.appointmentDate).toLocaleString(DateTime.DATETIME_MED) }}</span>
                <chip :label="rankMapping[review.rank]" unstyled class="py-1 rounded-lg text-xs px-1.5 w-max mt-1"
                      :class="{
              'bg-red-100 text-red-800': review.rank === 'not-good',
              'bg-green-100 text-green-800': review.rank === 'good',
              'bg-blue-100 text-blue-800': review.rank === 'ok',
            }" />
              </div>
              <span class="text-xs text-gray-800">
                <span v-if="providers.length > 1"> ע״י {{ providersMapping[review.providerId] }}</span>
              </span>
              <span class="text-gray-600 mt-3 text-sm">{{ review.review }}</span>
            </div>
            <Button plain outlined icon="pi pi-arrow-left" :pt="{icon: {class: '!text-gray-600'}}"
                    @click="goToAppointment(review.appointmentId, review.customerId)" />
          </div>
        </transition-group>
        <span class="text-center" v-if="reviews.length === 0">אין עדיין ביקורות</span>
      </t-card>
      <div class="h-2 flex-none"></div>
    </div>
    <div v-else class="w-full h-full flex flex-col justify-start items-center my-auto">
      <span class="text-sm">עוד אין ביקורות</span>
    </div>
  </div>
</template>

<script setup lang="ts">

import { ICustomer, IReviewsResponse, IReviewsSummary, rankMapping, TReactions } from '@torim-app/types';
import { DateTime } from 'luxon';
import { computed, onMounted, PropType, ref } from 'vue';
import { useAdminStore } from '@app/stores';
import { AdminRouteNames } from '@app/router/route-names';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { CustomersApi, ReviewsApi } from '@app/api/admin';
import { trackEvent } from '@app/composables/posthog';

const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true,
    default: () => {
    }
  }
});

const adminStore = useAdminStore();
const router = useRouter();
const toast = useToast();

const reviews = ref<IReviewsResponse['reviews']>([]);
const reviewsSummary = ref<IReviewsSummary>({} as IReviewsSummary);
const loading = ref<boolean>(true);

const providersMapping = computed<Record<string, string>>(() => {
  return Object.fromEntries(adminStore.getProviders.map(x => [x.userId, x.fullName]));
});
const providers = computed(() => adminStore.getProviders);

const goToAppointment = (apid: string, customerId: string) => {
  trackEvent('customer_review_appointment_clicked');
  router.push({
    name: AdminRouteNames.ClientProfile,
    params: { id: customerId },
    query: { apid }
  });
};

const loadReviews = async () => {
  loading.value = true;
  try {
    reviews.value = (await CustomersApi.fetchCustomerReviews(props.customer.id)).reviews;
    reviewsSummary.value = (await ReviewsApi.fetchReviewsSummary(undefined, props.customer.id));
  } catch (e) {
    console.log(e);
    toast.error('לא הצלחנו לטעון את הביקורות');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadReviews();
})
</script>

<style scoped lang="scss">

</style>
